<template>
  <div class="space-y-2">
    <!-- Debug: Log folder level -->
    <template v-if="false">{{ console.log(`FolderNode: ${folder.name}, Level: ${folder.level}`) }}</template>
    <!-- Folder Header -->
    <div 
      class="rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all duration-300 p-2"
      :style="{ marginLeft: `${folder.level * 24}px` }"
    >
      <div class="flex items-center justify-between pb-1">
        <div class="flex items-center space-x-2">
          <!-- Expand/Collapse Icon (only show if folder has children) -->
          <button
            v-if="folder.children && folder.children.length > 0"
            @click="toggleExpanded"
            class="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors focus:outline-none"
            :title="isExpanded ? 'Collapse folder' : 'Expand folder'"
          >
            <Icon :name="isExpanded ? 'chevron-down' : 'chevron-right'" size="sm" />
          </button>
          <!-- Spacer if no children -->
          <div v-else class="w-5"></div>
          
          <input
            type="checkbox"
            :checked="isFolderSelected"
            :indeterminate="isFolderIndeterminate"
            @change="toggleFolderSelection"
            class="w-4 h-4 text-blue-600 bg-white border-2 border-blue-300 rounded focus:ring-blue-500 focus:ring-2 transition-all duration-200 m-0"
          />
          <Icon name="folder" size="md" class="text-yellow-600 dark:text-yellow-400" />
            <div>
              <div class="flex items-center space-x-2">
                <span class="text-sm font-bold text-gray-700 dark:text-gray-300">{{ folder.name }}</span>
                <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                  {{ folder.testCases.length }}
                </span>
              </div>
            </div>
        </div>
      </div>
      
      <!-- Folder Test Cases Table -->
      <div v-if="folder.testCases.length > 0" class="mt-2 rounded-lg overflow-hidden">
        <div class="overflow-visible" style="overflow-x: visible !important;">
          <Table
            :columns="tableHeaders"
            :data="folder.testCases"
            class="folder-node-table w-full"
            style="overflow-x: visible !important;"
          >
           <template #cell-select="{ row }">
             <div class="flex justify-center p-0">
               <input
                 type="checkbox"
                 :checked="selectedTestCases.has(row.testCaseId)"
                 @change="$emit('toggle-test-case-selection', row.testCaseId)"
                 class="w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-800 dark:border-gray-600 m-0"
               />
             </div>
           </template>
          
          <template #cell-testCaseId="{ row }">
            <div class="flex justify-center">
              <span
                @click="$emit('open-test-case', row.testCaseId, $event)"
                class="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium cursor-pointer hover:underline bg-transparent border-none outline-none"
              >
                {{ row.testCaseId }}
              </span>
            </div>
          </template>
          
          <template #cell-testCase="{ row }">
            <div class="flex items-center gap-2 min-w-0 px-2">
              <span
                @click="$emit('open-test-case', row.testCaseId, $event)"
                class="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium cursor-pointer hover:underline text-left line-clamp-2 bg-transparent border-none outline-none"
                :title="row.testCaseName"
              >
                {{ row.testCaseName }}
              </span>
            </div>
          </template>
          
          <template #cell-type="{ row }">
            <div class="flex justify-center">
              <HLTag 
                :id="`testcase-type-${row.id}`" 
                :color="getHLTagColor(getTypeVariant(row.type))" 
                size="sm"
                round
                :bordered="false"
              >
                <template #default>{{ formatTypeLabel(row.type) }}</template>
              </HLTag>
            </div>
          </template>
          
          <template #cell-caseType="{ row }">
            <div class="flex justify-center">
              <HLTag 
                :id="`testcase-casetype-${row.id}`" 
                :color="getHLTagColor(getCaseTypeVariant(row.caseType))" 
                size="sm"
                round
                :bordered="false"
              >
                <template #default>{{ formatCaseTypeLabel(row.caseType) }}</template>
              </HLTag>
            </div>
          </template>
          
          <template #cell-automationStatus="{ row }">
            <div class="flex justify-center">
              <HLTag 
                :id="`testcase-automation-${row.id}`" 
                :color="getHLTagColor(getAutomationStatusVariant(row.automationStatus))" 
                size="sm"
                round
                :bordered="false"
              >
                <template #default>{{ formatAutomationLabel(row.automationStatus) }}</template>
              </HLTag>
            </div>
          </template>
          
          <template #cell-priority="{ row }">
            <div class="flex justify-center">
              <HLTag 
                :id="`testcase-priority-${row.id}`" 
                :color="getHLTagColor(getPriorityVariant(row.priority))" 
                size="sm"
                round
                :bordered="false"
              >
                <template #default>{{ formatPriorityLabel(row.priority) }}</template>
              </HLTag>
            </div>
          </template>
          
          <template #cell-estimatedTime="{ row }">
            <div class="flex justify-center">
              <span class="text-sm text-gray-600 dark:text-gray-400 font-medium">
                {{ formatEstimatedTime(row.estimatedTime) }}
              </span>
            </div>
          </template>
          
           <template #cell-status="{ row }">
             <div class="flex items-center justify-center w-full">
               <div 
                 class="status-select-wrapper"
                 :data-status-value="row.status?.toLowerCase().replace('_', '-')"
               >
               <HLSelect
                 :id="`status-${row.id || row.testCaseId}`"
                 v-model:value="row.status"
                 :options="statusOptions"
                   :tag-color="getStatusTagColorForOption(row.status)"
                 size="xs"
                   @update:value="(value) => handleStatusChange(row, value)"
                 style="min-width: 100px;"
                   class="status-select"
               />
               </div>
             </div>
           </template>
          
          <template #cell-assignee="{ row }">
            <div class="flex items-center justify-center w-full">
              <div 
                class="assignee-select-wrapper"
                :data-assignee-value="row.assigneeId || 'unassigned'"
                :style="{
                  '--assignee-bg': getAssigneeColorStyle(row.assigneeId).backgroundColor,
                  '--assignee-text': getAssigneeColorStyle(row.assigneeId).color,
                  '--assignee-border': getAssigneeColorStyle(row.assigneeId).borderColor
                }"
              >
              <HLSelect
                :id="`assignee-${row.id || row.testCaseId}`"
                v-model:value="row.assigneeId"
                :options="assigneeOptions"
                :tag-color="getAssigneeTagColorForOption(row.assigneeId)"
                :option-renderer="assigneeOptionRenderer"
                :filterable="true"
                size="xs"
                @update:value="(value) => handleAssigneeChange(row, value)"
                placeholder="Select Assignee"
                clearable
                style="min-width: 100px;"
                class="assignee-select"
              />
              </div>
              <span v-if="!row.assigneeId" class="sr-only">Unassigned</span>
            </div>
          </template>
          
          <template #cell-executedAt="{ row }">
            <div class="text-center">
              <span class="text-xs text-gray-600 dark:text-gray-400">
                {{ row.executed_at ? formatDate(row.executed_at) : 'Yet to start' }}
              </span>
            </div>
          </template>
          
          <template #cell-executedBy="{ row }">
            <div class="text-center">
              <span class="text-xs text-gray-600 dark:text-gray-400">
                {{ getExecutedByDisplayName(row.executed_by) }}
              </span>
            </div>
          </template>
          
          <template #cell-bugs="{ row }">
            <div class="flex justify-center px-2">
              <div class="relative w-full" style="min-width: 200px; max-width: 250px;">
                <div class="relative">
                  <!-- Selected bugs display with + button -->
                  <div class="flex items-start gap-1">
                    <!-- Selected bugs as HLTag -->
                    <div v-if="row.bugIds && row.bugIds.length > 0" class="flex flex-wrap gap-1 flex-1">
                      <HLTag
                        v-for="bugId in row.bugIds"
                        :key="bugId"
                        :color="getBugTagColor(bugId)"
                        size="sm"
                        round
                        :bordered="false"
                        :closable="true"
                        @click="viewBug(bugId, $event)"
                        @close="removeBug(row, bugId)"
                        class="cursor-pointer hover:opacity-80 transition-opacity"
                      >
                        <template #default>
                          {{ getBugLabel(bugId) }}
                        </template>
                      </HLTag>
                    </div>
                    
                    <!-- Empty state when no bugs -->
                    <div v-else class="text-xs text-gray-400 dark:text-gray-500 italic flex-1">
                      No bugs
                    </div>
                    
                    <!-- + button to show dropdown (always visible) -->
                    <div class="relative flex-shrink-0">
                      <HLPopover
                        :id="`bug-dropdown-${row.testCaseId}`"
                        v-model:show="showBugDropdown[row.testCaseId]"
                        trigger="click"
                        placement="bottom-end"
                        :show-arrow="false"
                        raw
                        :width="280"
                      >
                        <template #trigger>
                      <HLButton
                        variant="text"
                        color="primary"
                        size="sm"
                        :title="'Add or create bug'"
                        class="px-1"
                            @click.stop="toggleBugDropdown(row)"
                      >
                        <Icon name="plus" size="sm" />
                      </HLButton>
                        </template>
                        <template #default>
                          <div class="p-2 bg-white dark:bg-gray-800">
                          <!-- Search/filter input -->
                            <HLInput
                              :id="`bug-search-${row.testCaseId}`"
                            v-model="bugSearchQuery[row.testCaseId]"
                            type="text"
                            placeholder="Search bugs..."
                              size="sm"
                              class="w-full mb-2"
                            @click.stop
                          />
                          
                          <!-- Bug list -->
                            <div class="space-y-1 max-h-48 overflow-y-auto">
                            <button
                              v-for="bug in getFilteredBugs(row)"
                              :key="bug.id"
                                @click.stop="toggleBug(row, bug.id)"
                              class="w-full flex items-center justify-between text-left px-2 py-1.5 text-xs text-gray-900 dark:text-gray-100 cursor-pointer bg-transparent border-none outline-none hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors rounded"
                            >
                              <div class="flex-1 pr-2" style="word-break: break-word;">
                                <span class="font-medium">{{ bug.bugId || bug.id }}</span>
                                <span class="text-gray-600 dark:text-gray-400"> - {{ bug.title }}</span>
                              </div>
                              <div class="flex-shrink-0">
                                <svg 
                                  v-if="row.bugIds && row.bugIds.includes(bug.id)"
                                  class="w-4 h-4 text-blue-600 dark:text-blue-400" 
                                  fill="currentColor" 
                                  viewBox="0 0 20 20"
                                >
                                  <path 
                                    fill-rule="evenodd" 
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                                    clip-rule="evenodd"
                                  />
                                </svg>
                              </div>
                            </button>
                          </div>
                          
                          <!-- Divider -->
                          <div class="border-t border-gray-200 dark:border-gray-700 my-2"></div>
                          
                          <!-- Create new bug option - HLButton -->
                          <HLButton
                              @click.stop="createNewBug(row)"
                            variant="text"
                            color="primary"
                            size="sm"
                            class="w-full justify-start px-2"
                          >
                            <template #iconLeft>
                              <Icon name="plus" size="sm" />
                            </template>
                            Create New Bug
                          </HLButton>
                        </div>
                        </template>
                      </HLPopover>
                    </div>
                  </div>
                  
                  <!-- Read-only view -->
                  <div v-if="!canEdit && row.bugIds && row.bugIds.length > 0" class="flex flex-wrap gap-1">
                    <button 
                      v-for="bugId in row.bugIds" 
                      :key="bugId"
                      @click="viewBug(bugId, $event)"
                      :class="getBugBadgeClasses(bugId)"
                      class="inline-flex items-center px-2 py-0.5 rounded text-xs hover:underline cursor-pointer transition-colors"
                      :title="'View bug details: ' + getBugLabel(bugId)"
                    >
                      {{ getBugLabel(bugId) }}
                    </button>
                  </div>
                  
                  <!-- Validation message -->
                  <p v-if="(row.status === 'failed' || row.status === 'blocked') && (!row.bugIds || row.bugIds.length === 0)" 
                     class="text-xs text-red-600 dark:text-red-400 mt-1 font-medium">
                    Bug required *
                  </p>
                </div>
              </div>
            </div>
          </template>
          
          <template #cell-notes="{ row }">
            <div class="flex justify-center px-2">
              <textarea
                v-if="canEdit"
                v-model="row.notes"
                @blur="$emit('update-test-case-notes', row)"
                placeholder="Add notes..."
                rows="2"
                class="w-full text-xs px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 resize-y"
                style="min-width: 180px; max-width: 250px; min-height: 40px;"
              ></textarea>
              <div v-else-if="row.notes" class="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap" style="min-width: 180px; max-width: 250px;">
                {{ row.notes }}
              </div>
              <div v-else class="text-xs text-gray-400 dark:text-gray-500 italic">
                No notes
              </div>
            </div>
          </template>
          
        </Table>
        </div>
      </div>
    </div>
    
    <!-- Child Folders (only render if expanded and within depth limit) -->
    <template v-if="isExpanded && folder.children && folder.children.length > 0 && folder.level < maxDepth">
    <FolderNode
      v-for="childFolder in folder.children"
      :key="childFolder.id"
      :folder="childFolder"
      :table-headers="tableHeaders"
      :selected-test-cases="selectedTestCases"
      :team-members="teamMembers"
      :available-bugs="availableBugs"
      :can-edit="canEdit"
        :max-depth="maxDepth"
      @toggle-test-case-selection="$emit('toggle-test-case-selection', $event)"
      @toggle-folder-selection="$emit('toggle-folder-selection', $event)"
      @update-test-case-status="$emit('update-test-case-status', $event)"
      @update-test-case-assignee="$emit('update-test-case-assignee', $event)"
      @update-test-case-bugs="$emit('update-test-case-bugs', $event)"
      @update-test-case-notes="$emit('update-test-case-notes', $event)"
      @create-bug-for-test-case="$emit('create-bug-for-test-case', $event)"
      @view-bug="$emit('view-bug', $event)"
      @execute-test-case="$emit('execute-test-case', $event)"
      @edit-test-case="$emit('edit-test-case', $event)"
      @remove-test-case="$emit('remove-test-case', $event)"
      @open-test-case="$emit('open-test-case', $event)"
    />
    </template>
    
    <!-- Max depth warning -->
    <div 
      v-if="isExpanded && folder.children && folder.children.length > 0 && folder.level >= maxDepth"
      class="ml-12 p-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded"
    >
      ⚠️ Maximum nesting depth reached. Some nested folders may not be displayed.
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount, onUpdated, nextTick, watch, h } from 'vue'
import { TEST_CASE_EXECUTION_STATUS } from '@/constants/testCaseConstants'
import { HLBadge, HLButton, HLTag, HLSelect, HLPopover, HLInput } from '@platform-ui/highrise'
import { getHLTagColor } from '@/utils/badgeHelpers'
import { formatBugStatus } from '@/utils/bugHelpers'
import { getBugStatusHLTag, getBugStatusBadge } from '@/utils/badgeHelpers'
import { getStatusColor as getStatusColorFromConstants } from '@/constants/statusColors'
import Table from '@/components/ui/Table.vue'
import Icon from '@/components/ui/Icon.vue'

// Reactive state for dropdowns
const showBugDropdown = ref({})
const bugSearchQuery = ref({})

const props = defineProps({
  folder: {
    type: Object,
    required: true
  },
  tableHeaders: {
    type: Array,
    required: true
  },
  selectedTestCases: {
    type: Set,
    required: true
  },
  teamMembers: {
    type: Array,
    default: () => []
  },
  availableBugs: {
    type: Array,
    default: () => []
  },
  canEdit: {
    type: Boolean,
    default: false
  },
  maxDepth: {
    type: Number,
    default: 999 // Effectively unlimited depth (999 levels is more than any realistic use case)
  }
})

// Folder expansion state
const isExpanded = ref(true) // Start expanded by default

// Toggle folder expansion
const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value
  console.log(`🔵 [FolderNode] Toggle expansion for "${props.folder.name}":`, {
    isExpanded: isExpanded.value,
    hasChildren: props.folder.children?.length > 0,
    childrenCount: props.folder.children?.length || 0,
    level: props.folder.level,
    maxDepth: props.maxDepth
  })
}

const emit = defineEmits([
  'toggle-test-case-selection',
  'toggle-folder-selection',
  'update-test-case-status',
  'update-test-case-assignee',
  'update-test-case-bugs',
  'update-test-case-notes',
  'create-bug-for-test-case',
  'open-test-case',
  'view-bug'
])

// Toggle bug dropdown visibility
const toggleBugDropdown = (row) => {
  const testCaseId = row.testCaseId
  // Toggle the dropdown state
  if (!showBugDropdown.value[testCaseId]) {
    showBugDropdown.value[testCaseId] = true
  // Initialize search query if not exists
  if (!bugSearchQuery.value[testCaseId]) {
    bugSearchQuery.value[testCaseId] = ''
  }
  } else {
    showBugDropdown.value[testCaseId] = false
    // Clear search when closing
    bugSearchQuery.value[testCaseId] = ''
  }
}

// Get filtered bugs based on search query
const getFilteredBugs = (row) => {
  const searchQuery = bugSearchQuery.value[row.testCaseId] || ''
  if (!searchQuery) {
    return props.availableBugs
  }
  
  const query = searchQuery.toLowerCase()
  return props.availableBugs.filter(bug => {
    const bugId = (bug.bugId || bug.id).toLowerCase()
    const title = bug.title.toLowerCase()
    return bugId.includes(query) || title.includes(query)
  })
}

// Toggle a bug (add or remove)
const toggleBug = (row, bugId) => {
  // Initialize bugIds if not exists
  if (!row.bugIds) {
    row.bugIds = []
  }
  
  // Toggle bug selection
  if (row.bugIds.includes(bugId)) {
    // Remove bug
    row.bugIds = row.bugIds.filter(id => id !== bugId)
  } else {
    // Add bug
    row.bugIds.push(bugId)
  }
  
  emit('update-test-case-bugs', row)
  
  // Don't close dropdown - allow multiple selections
}

// Add a bug to the list (kept for backward compatibility)
const addBug = (row, bugId) => {
  // Initialize bugIds if not exists
  if (!row.bugIds) {
    row.bugIds = []
  }
  
  // Add bug if not already added
  if (!row.bugIds.includes(bugId)) {
    row.bugIds.push(bugId)
    emit('update-test-case-bugs', row)
  }
}

// Remove a bug from the list
const removeBug = (row, bugId) => {
  if (row.bugIds) {
    row.bugIds = row.bugIds.filter(id => id !== bugId)
    emit('update-test-case-bugs', row)
  }
}

// Create new bug for test case
const createNewBug = (row) => {
  emit('create-bug-for-test-case', row)
  // Close dropdown
  showBugDropdown.value[row.testCaseId] = false
  bugSearchQuery.value[row.testCaseId] = ''
}

// Get bug label by ID (with status)
const getBugLabel = (bugId) => {
  const bug = props.availableBugs.find(b => b.id === bugId)
  if (bug) {
    const bugIdLabel = bug.bugId || bug.id
    // Include status if available
    if (bug.status) {
      const formattedStatus = formatBugStatus(bug.status)
      return `${bugIdLabel} - ${formattedStatus}`
    }
    return bugIdLabel
  }
  return bugId
}

// Get bug object by ID
const getBugById = (bugId) => {
  return props.availableBugs.find(b => b.id === bugId)
}

// Get bug color for HLTag based on status
// Get HLTag color for bug status using centralized utility
const getBugTagColor = (bugId) => {
  const bug = getBugById(bugId)
  if (!bug || !bug.status) {
    return 'error' // Default red if no status
  }
  
  // Use centralized utility function from badgeHelpers
  const hlTagConfig = getBugStatusHLTag(bug.status)
  return hlTagConfig.color || 'error'
}

// Get bug badge classes for read-only view using centralized utility
const getBugBadgeClasses = (bugId) => {
  const bug = getBugById(bugId)
  if (!bug || !bug.status) {
    // Default red if no status
    return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50'
  }
  
  // Use centralized utility function from badgeHelpers
  const badgeConfig = getBugStatusBadge(bug.status)
  return badgeConfig.class || 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50'
}

// View bug details
const viewBug = (bugId, event) => {
  emit('view-bug', bugId, event)
}

// Function to directly apply colors to select elements via DOM manipulation
const applySelectColors = () => {
  // Apply status colors
  props.folder.testCases.forEach((testCase) => {
    const status = testCase.status?.toLowerCase().replace('_', '-') || 'not-started'
    const selectId = `status-${testCase.id || testCase.testCaseId}`
    const selectElement = document.getElementById(selectId)
    
    if (selectElement) {
      const statusColors = {
        'failed': { bg: '#fee2e2', text: '#dc2626', border: '#dc2626' },
        'pass': { bg: '#dcfce7', text: '#16a34a', border: '#16a34a' },
        'passed': { bg: '#dcfce7', text: '#16a34a', border: '#16a34a' },
        'blocked': { bg: '#fef3c7', text: '#d97706', border: '#d97706' },
        'not-started': { bg: '#f3f4f6', text: '#6b7280', border: '#6b7280' },
        'in-progress': { bg: '#dbeafe', text: '#2563eb', border: '#2563eb' },
        'skipped': { bg: '#e9d5ff', text: '#9333ea', border: '#9333ea' }
      }
      
      const colors = statusColors[status] || statusColors['not-started']
      
      // Find trigger button inside the select - try multiple selectors
      const trigger = selectElement.querySelector('[class*="trigger"]') || 
                     selectElement.querySelector('button') ||
                     selectElement.querySelector('div[role="button"]') ||
                     selectElement.querySelector('div[role="combobox"]') ||
                     selectElement.querySelector(':scope > div:first-child')
      
      if (trigger) {
        trigger.style.setProperty('background-color', colors.bg, 'important')
        trigger.style.setProperty('color', colors.text, 'important')
        trigger.style.setProperty('border', `1px solid ${colors.border}`, 'important')
        trigger.style.setProperty('border-radius', '0.375rem', 'important')
        
        // Style all child elements
        const children = trigger.querySelectorAll('*')
        children.forEach(child => {
          child.style.setProperty('color', colors.text, 'important')
          if (child.tagName === 'SVG' || child.tagName === 'svg') {
            child.style.setProperty('fill', colors.text, 'important')
            child.style.setProperty('stroke', colors.text, 'important')
          }
        })
      }
    }
  })
  
  // Apply assignee colors
  props.folder.testCases.forEach((testCase) => {
    const assigneeId = testCase.assigneeId || ''
    const selectId = `assignee-${testCase.id || testCase.testCaseId}`
    const selectElement = document.getElementById(selectId)
    
    if (selectElement) {
      const assigneeColors = getAssigneeColorStyle(assigneeId)
      
      // Find trigger button inside the select
      const trigger = selectElement.querySelector('[class*="trigger"]') || 
                     selectElement.querySelector('button') ||
                     selectElement.querySelector('div[role="button"]') ||
                     selectElement.querySelector('div[role="combobox"]') ||
                     selectElement.querySelector(':scope > div:first-child')
      
      if (trigger) {
        trigger.style.setProperty('background-color', assigneeColors.backgroundColor, 'important')
        trigger.style.setProperty('color', assigneeColors.color, 'important')
        trigger.style.setProperty('border', `1px solid ${assigneeColors.borderColor}`, 'important')
        trigger.style.setProperty('border-radius', '0.375rem', 'important')
        
        // Style all child elements
        const children = trigger.querySelectorAll('*')
        children.forEach(child => {
          child.style.setProperty('color', assigneeColors.color, 'important')
          if (child.tagName === 'SVG' || child.tagName === 'svg') {
            child.style.setProperty('fill', assigneeColors.color, 'important')
            child.style.setProperty('stroke', assigneeColors.color, 'important')
          }
        })
      }
    }
  })
}

// Setup lifecycle hooks
onMounted(() => {
  // Debug: Log folder structure on mount
  console.log(`🔵 [FolderNode] Mounted "${props.folder.name}":`, {
    level: props.folder.level,
    testCasesCount: props.folder.testCases?.length || 0,
    hasChildren: props.folder.children?.length > 0,
    childrenCount: props.folder.children?.length || 0,
    children: props.folder.children?.map(c => ({ name: c.name, testCases: c.testCases?.length || 0 })) || [],
    isExpanded: isExpanded.value,
    maxDepth: props.maxDepth
  })
  
  // Pre-load all executed_by user names
  loadExecutedByNames()
  // Apply colors to selects after mount
  nextTick(() => {
    applySelectColors()
  })
})

onUpdated(() => {
  // Re-apply colors when component updates (e.g., when status/assignee changes)
  nextTick(() => {
    applySelectColors()
  })
})

// Watch for changes in test cases (especially status and assignee changes)
watch(() => props.folder.testCases, () => {
  nextTick(() => {
    applySelectColors()
    // Also try after a short delay to ensure DOM is fully updated
    setTimeout(() => {
      applySelectColors()
    }, 100)
  })
}, { deep: true })

// Helper functions
const getTypeVariant = (type) => {
  const variants = {
    'functional': 'primary',
    'api': 'warning',
    'ui': 'info',
    'performance': 'secondary'
  }
  return variants[type] || 'secondary'
}

const getCaseTypeVariant = (caseType) => {
  const variants = {
    'sanity': 'primary',
    'smoke': 'secondary',
    'regression': 'warning'
  }
  return variants[caseType] || 'primary'
}

const getAutomationStatusVariant = (automationStatus) => {
  const variants = {
    'automated': 'success',
    'manual': 'secondary',
    'semi-automated': 'warning'
  }
  return variants[automationStatus] || 'secondary'
}

const getPriorityVariant = (priority) => {
  const variants = {
    'critical': 'danger',
    'high': 'warning',
    'medium': 'secondary',
    'low': 'success'
  }
  return variants[priority] || 'secondary'
}

// Note: getHLTagColor is now imported from @/utils/badgeHelpers

// Format label functions
const formatTypeLabel = (type) => {
  const labels = {
    'functional': 'Functional',
    'api': 'API',
    'ui': 'UI',
    'performance': 'Performance',
    'security': 'Security',
    'non-functional': 'Non-Functional'
  }
  return labels[type] || type
}

const formatCaseTypeLabel = (caseType) => {
  const labels = {
    'sanity': 'Sanity',
    'smoke': 'Smoke',
    'regression': 'Regression'
  }
  return labels[caseType] || caseType
}

const formatAutomationLabel = (automationStatus) => {
  const labels = {
    'automated': 'Automated',
    'manual': 'Manual',
    'semi-automated': 'Semi-Automated'
  }
  return labels[automationStatus] || automationStatus
}

const formatPriorityLabel = (priority) => {
  const labels = {
    'critical': 'Critical',
    'high': 'High',
    'medium': 'Medium',
    'low': 'Low'
  }
  return labels[priority] || priority
}

const formatEstimatedTime = (time) => {
  if (!time) return '0 mins'
  
  // If time is already formatted (contains 'h' or 'm'), return as is
  if (typeof time === 'string' && (time.includes('h') || time.includes('m'))) {
    return time
  }
  
  // Convert to number if it's a string
  const timeNum = typeof time === 'number' ? time : parseInt(time) || 0
  
  if (timeNum === 0) return '0 mins'
  if (timeNum < 60) return `${timeNum} mins`
  
  // Convert to hours and minutes
  const hours = Math.floor(timeNum / 60)
  const minutes = timeNum % 60
  
  if (minutes === 0) {
    return hours === 1 ? '1 hour' : `${hours} hours`
  }
  
  return hours === 1 ? `1 hour ${minutes} mins` : `${hours} hours ${minutes} mins`
}

const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Cache for loaded user names
const userNameCache = ref({})

const getExecutedByDisplayName = (userId) => {
  // Return dash if no userId
  if (!userId) return '-'
  
  // Check cache first
  if (userNameCache.value[userId]) {
    return userNameCache.value[userId]
  }
  
  // Search in team members
  const member = props.teamMembers.find(m => m.id === userId)
  if (member) {
    const name = member.name || member.email || userId
    userNameCache.value[userId] = name
    return name
  }
  
  // Return userId if not found yet (will be loaded by loadExecutedByNames)
  return userId
}

// Load all executed_by user names from database
const loadExecutedByNames = async () => {
  try {
    // Collect all unique executed_by IDs
    const executedByIds = new Set()
    props.folder.testCases.forEach(tc => {
      if (tc.executed_by && !userNameCache.value[tc.executed_by]) {
        executedByIds.add(tc.executed_by)
      }
    })
    
    // Load user details for those not in teamMembers
    const { userService } = await import('@/database/services')
    for (const userId of executedByIds) {
      try {
        const user = await userService.getById(userId)
        if (user) {
          userNameCache.value[userId] = user.name || user.email || userId
        }
      } catch (error) {
        // Skip if user not found
      }
    }
  } catch (error) {
    // Silently fail
  }
}


// Folder selection helpers
const getAllTestCasesInFolder = (folder) => {
  let testCases = [...folder.testCases]
  if (folder.children) {
    folder.children.forEach(child => {
      testCases = testCases.concat(getAllTestCasesInFolder(child))
    })
  }
  return testCases
}

const isFolderSelected = computed(() => {
  const allTestCases = getAllTestCasesInFolder(props.folder)
  return allTestCases.length > 0 && 
         allTestCases.every(tc => props.selectedTestCases.has(tc.testCaseId))
})

const isFolderIndeterminate = computed(() => {
  const allTestCases = getAllTestCasesInFolder(props.folder)
  const selectedCount = allTestCases.filter(tc => props.selectedTestCases.has(tc.testCaseId)).length
  return selectedCount > 0 && selectedCount < allTestCases.length
})

const toggleFolderSelection = () => {
  emit('toggle-folder-selection', props.folder.id)
}

// Get status tag color for options
const getStatusTagColorForOption = (statusValue) => {
  const statusLower = (statusValue || '').toLowerCase()
  const colorMap = {
    'failed': 'error', // red
    'pass': 'success', // green
    'passed': 'success', // green
    'blocked': 'warning', // yellow/orange
    'not_started': 'gray', // gray
    'in_progress': 'primary', // blue
    'skipped': 'purple' // purple
  }
  return colorMap[statusLower] || 'gray'
}

// Computed options for HLSelect components with tag colors
const statusOptions = computed(() => 
  TEST_CASE_EXECUTION_STATUS.map(status => ({
    label: status.label,
    value: status.value,
    tagColor: getStatusTagColorForOption(status.value),
    tagProps: {
      color: getStatusTagColorForOption(status.value),
      size: 'xs',
      bordered: false,
      round: true
    }
  }))
)

// Get assignee tag color (consistent per assignee)
const assigneeTagColorCache = {}
const assigneeTagColors = ['primary', 'purple', 'warning', 'success', 'purple', 'warning', 'primary', 'warning', 'purple', 'error']

const getAssigneeTagColorForOption = (assigneeId) => {
  if (!assigneeId) return 'gray'
  
  if (!assigneeTagColorCache[assigneeId]) {
    let hash = 0
    for (let i = 0; i < assigneeId.length; i++) {
      hash = assigneeId.charCodeAt(i) + ((hash << 5) - hash)
    }
    const index = Math.abs(hash) % assigneeTagColors.length
    assigneeTagColorCache[assigneeId] = assigneeTagColors[index]
  }
  
  return assigneeTagColorCache[assigneeId]
}

const assigneeOptions = computed(() => {
  const options = [
    { 
      label: 'Unassigned', 
      value: '',
      tagColor: 'gray',
      tagProps: {
        color: 'gray',
        size: 'xs',
        bordered: false,
        round: true
      }
    }
  ]
  
  // Add all team members, but filter out "Unknown User" entries
  props.teamMembers.forEach(member => {
    // Skip if the member is an "Unknown User" placeholder
    if (member.name === 'Unknown User' && member.email === member.id) {
      return
    }
    
    // Only add valid members with actual names or emails
    if (member.name || member.email) {
      const tagColor = getAssigneeTagColorForOption(member.id)
    options.push({
      label: member.name || member.email,
        value: member.id,
        tagColor: tagColor,
        tagProps: {
          color: tagColor,
          size: 'xs',
          bordered: false,
          round: true
        }
    })
    }
  })
  
  return options
})

// Custom option renderer for assignee dropdown to fix checkmark spacing
const assigneeOptionRenderer = (option) => {
  if (option.renderOption) {
    return option.renderOption()
  }
  
  // Create a custom render that properly spaces the text to leave room for checkmark
  return h('div', {
    class: 'flex items-center w-full',
    style: 'position: relative; padding-right: 1.75rem;'
  }, [
    h('span', {
      class: 'flex-1',
      style: 'padding-right: 0.5rem; max-width: calc(100% - 1.5rem); overflow: hidden; text-overflow: ellipsis; white-space: nowrap;'
    }, option.label || '')
  ])
}

// Handle status change
const handleStatusChange = (row, newValue) => {
  row.status = newValue
  emit('update-test-case-status', row)
  // Re-apply colors after status change
  nextTick(() => {
    applySelectColors()
    // Also try after a short delay to ensure DOM is fully updated
    setTimeout(() => {
      applySelectColors()
    }, 100)
  })
}

// Handle assignee change
const handleAssigneeChange = (row, newValue) => {
  // v-model already updates row.assigneeId automatically
  // But we need to ensure it's set correctly before emitting
  // Normalize: empty string for unassigned, or the assignee ID
  const normalizedValue = (newValue === null || newValue === undefined) ? '' : newValue
  // Update the row with the normalized value
  row.assigneeId = normalizedValue
  // Emit the updated row immediately
  emit('update-test-case-assignee', { ...row, assigneeId: normalizedValue })
  // Re-apply colors after assignee change
  nextTick(() => {
    applySelectColors()
    // Also try after a short delay to ensure DOM is fully updated
    setTimeout(() => {
      applySelectColors()
    }, 100)
  })
}

// Get status cell style (for CSS variables)
const getStatusCellStyle = (status) => {
  const statusLower = (status || '').toLowerCase().replace('_', '-')
  const colorMap = {
    'failed': { bg: '#fee2e2', text: '#dc2626', border: '#dc2626' },
    'pass': { bg: '#dcfce7', text: '#16a34a', border: '#16a34a' },
    'passed': { bg: '#dcfce7', text: '#16a34a', border: '#16a34a' },
    'blocked': { bg: '#fef3c7', text: '#d97706', border: '#d97706' },
    'not-started': { bg: '#f3f4f6', text: '#6b7280', border: '#6b7280' },
    'in-progress': { bg: '#dbeafe', text: '#2563eb', border: '#2563eb' },
    'skipped': { bg: '#e9d5ff', text: '#9333ea', border: '#9333ea' }
  }
  
  const colors = colorMap[statusLower] || colorMap['not-started']
  
  return {
    '--status-bg': colors.bg,
    '--status-text': colors.text,
    '--status-border': colors.border
  }
}

// Get assignee color for styling (consistent per assignee)
const assigneeColorCache = {}
const assigneeColors = [
  { bg: '#dbeafe', text: '#1e40af', border: '#1e40af' }, // blue
  { bg: '#fce7f3', text: '#9f1239', border: '#9f1239' }, // pink
  { bg: '#fef3c7', text: '#92400e', border: '#92400e' }, // yellow
  { bg: '#d1fae5', text: '#065f46', border: '#065f46' }, // green
  { bg: '#e9d5ff', text: '#6b21a8', border: '#6b21a8' }, // purple
  { bg: '#fed7aa', text: '#9a3412', border: '#9a3412' }, // orange
  { bg: '#cffafe', text: '#155e75', border: '#155e75' }, // cyan
  { bg: '#fde68a', text: '#78350f', border: '#78350f' }, // amber
  { bg: '#e0e7ff', text: '#3730a3', border: '#3730a3' }, // indigo
  { bg: '#fecaca', text: '#991b1b', border: '#991b1b' }  // red
]

const getAssigneeColorStyle = (assigneeId) => {
  if (!assigneeId) {
    return {
      backgroundColor: '#f3f4f6',
      color: '#6b7280',
      borderColor: '#6b7280'
    }
  }
  
  if (!assigneeColorCache[assigneeId]) {
    // Generate consistent color based on assignee ID
    let hash = 0
    for (let i = 0; i < assigneeId.length; i++) {
      hash = assigneeId.charCodeAt(i) + ((hash << 5) - hash)
    }
    const index = Math.abs(hash) % assigneeColors.length
    assigneeColorCache[assigneeId] = assigneeColors[index]
  }
  
  return assigneeColorCache[assigneeId]
}
</script>

<style scoped>
/* FolderNode Table Styling - Match HLDataTable Design */

/* Main table wrapper */
:deep(.folder-node-table) {
  width: 100% !important;
  max-width: none !important;
  background-color: white !important;
  border-collapse: collapse;
  border-top: 1px solid rgb(229 231 235) !important;
}

:deep(.dark .folder-node-table) {
  background-color: rgb(31 41 55) !important;
  border-top: 1px solid rgb(55 65 81) !important;
}

/* Remove padding from wrapper divs */
:deep(.folder-node-table).overflow-visible {
  padding: 0 !important;
  margin: 0 !important;
}

/* Table header styling */
:deep(.folder-node-table .table th) {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.05em;
  color: rgb(75 85 99) !important;
  background-color: rgb(249 250 251) !important;
  padding: 1rem;
  white-space: normal;
  word-wrap: break-word;
  border-left: none !important;
  border-right: none !important;
  border-top: none !important;
  border-bottom: 1px solid rgb(229 231 235) !important;
}

:deep(.dark .folder-node-table .table th) {
  color: rgb(209 213 219) !important;
  background-color: rgb(31 41 55) !important;
  border-bottom: 1px solid rgb(55 65 81) !important;
}

/* Table cell styling */
:deep(.folder-node-table .table td) {
  padding: 1rem;
  vertical-align: middle;
  border-left: none !important;
  border-right: none !important;
  border-top: none !important;
  border-bottom: 1px solid rgb(243 244 246) !important;
  font-size: 12px !important;
}

:deep(.dark .folder-node-table .table td) {
  border-bottom: 1px solid rgb(55 65 81) !important;
}

/* First and last cell padding */
:deep(.folder-node-table .table th:first-child) {
  padding-left: 2rem !important;
}

:deep(.folder-node-table .table td:first-child) {
  padding-left: 2rem !important;
}

:deep(.folder-node-table .table th:last-child) {
  padding-right: 2rem !important;
}

:deep(.folder-node-table .table td:last-child) {
  padding-right: 2rem !important;
}

/* Table row styling */
:deep(.folder-node-table .table tbody tr) {
  height: auto;
  min-height: 56px;
  line-height: 1.5;
}

:deep(.folder-node-table .table tbody tr:last-child td) {
  border-bottom: 1px solid rgb(229 231 235) !important;
}

:deep(.dark .folder-node-table .table tbody tr:last-child td) {
  border-bottom: 1px solid rgb(55 65 81) !important;
}

/* Hover effect */
:deep(.folder-node-table .table tbody tr:hover) {
  background-color: rgb(249 250 251) !important;
}

:deep(.dark .folder-node-table .table tbody tr:hover) {
  background-color: rgb(37 47 63) !important;
}

/* Font sizes for all table content */
:deep(.folder-node-table td span),
:deep(.folder-node-table td button),
:deep(.folder-node-table td input),
:deep(.folder-node-table td select),
:deep(.folder-node-table td textarea),
:deep(.folder-node-table td div),
:deep(.folder-node-table td a) {
  font-size: 12px !important;
}

/* HLSelect and HLTag sizing */
:deep(.folder-node-table .hl-select),
:deep(.folder-node-table .hl-select .hl-select__trigger),
:deep(.folder-node-table .hl-select__content),
:deep(.folder-node-table .hl-select-option) {
  font-size: 12px !important;
}

:deep(.folder-node-table .hl-tag),
:deep(.folder-node-table .hl-tag__content) {
  font-size: 12px !important;
}

/* General cell content sizing */
:deep(.folder-node-table [class*="cell-"]),
:deep(.folder-node-table [class*="cell-"] *) {
  font-size: 12px !important;
}

:deep(.folder-node-table td *) {
  font-size: 12px !important;
}

/* Center align text in HLSelect dropdowns */
:deep(.folder-node-table .hr-select .hr-base-selection-input__content) {
  text-align: center !important;
}

/* Ensure dropdown text is centered */
:deep(.folder-node-table .hr-select .hr-base-selection-label) {
  justify-content: center !important;
}

:deep(.folder-node-table .hr-select .hr-base-selection-input) {
  text-align: center !important;
}

/* Fix checkmark overlapping text in HLSelect dropdown options */

/* Target dropdown containers - add padding for checkmark */
:deep(.folder-node-table .hl-select [class*="overlay"]),
:deep(.folder-node-table .hl-select [class*="overlay"] *),
:deep(.folder-node-table .hl-select [class*="menu"]),
:deep(.folder-node-table .hl-select [class*="menu"] *),
:deep(.folder-node-table .hl-select [class*="content"]),
:deep(.folder-node-table .hl-select [class*="content"] *),
:deep(.folder-node-table .hl-select [class*="option"]),
:deep(.folder-node-table .hl-select [class*="item"]),
:deep(.folder-node-table .hl-select [class*="Option"]),
:deep(.folder-node-table .hl-select [class*="Item"]) {
  position: relative !important;
}

/* Add right padding to any dropdown option-like element */
:deep(.folder-node-table .hl-select [class*="overlay"] > div),
:deep(.folder-node-table .hl-select [class*="overlay"] > div > div),
:deep(.folder-node-table .hl-select [class*="menu"] > div),
:deep(.folder-node-table .hl-select [class*="menu"] > div > div),
:deep(.folder-node-table .hl-select [class*="content"] > div),
:deep(.folder-node-table .hl-select [class*="content"] > div > div),
:deep(.folder-node-table .hl-select [class*="option"]),
:deep(.folder-node-table .hl-select [class*="item"]) {
  padding-right: 2.25rem !important;
}

/* Position ALL SVGs in dropdown (except trigger button) absolutely on the right */
:deep(.folder-node-table .hl-select [class*="overlay"] svg),
:deep(.folder-node-table .hl-select [class*="menu"] svg),
:deep(.folder-node-table .hl-select [class*="content"] svg),
:deep(.folder-node-table .hl-select [class*="option"] svg),
:deep(.folder-node-table .hl-select [class*="item"] svg) {
  position: absolute !important;
  right: 0.5rem !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  pointer-events: none !important;
  width: 1rem !important;
  height: 1rem !important;
  flex-shrink: 0 !important;
  margin-left: 0.5rem !important;
}

/* Ensure text/spans don't extend into checkmark area */
:deep(.folder-node-table .hl-select [class*="overlay"] span),
:deep(.folder-node-table .hl-select [class*="menu"] span),
:deep(.folder-node-table .hl-select [class*="content"] span),
:deep(.folder-node-table .hl-select [class*="option"] span),
:deep(.folder-node-table .hl-select [class*="item"] span) {
  padding-right: 0.75rem !important;
  max-width: calc(100% - 2.25rem) !important;
  display: inline-block !important;
  box-sizing: border-box !important;
}

/* Status dropdown colors - Direct styling based on data attribute */
/* Failed - Red */
:deep(.folder-node-table [data-status-value="failed"] .hl-select [class*="trigger"]),
:deep(.folder-node-table [data-status-value="failed"] .hl-select [class*="button"]),
:deep(.folder-node-table [data-status-value="failed"] .hl-select > div:first-child),
:deep(.folder-node-table [data-status-value="failed"] .hl-select > button) {
  background-color: #fee2e2 !important;
  color: #dc2626 !important;
  border: 1px solid #dc2626 !important;
  border-radius: 0.375rem !important;
}

/* Passed - Green */
:deep(.folder-node-table [data-status-value="passed"] .hl-select [class*="trigger"]),
:deep(.folder-node-table [data-status-value="passed"] .hl-select [class*="button"]),
:deep(.folder-node-table [data-status-value="passed"] .hl-select > div:first-child),
:deep(.folder-node-table [data-status-value="passed"] .hl-select > button),
:deep(.folder-node-table [data-status-value="pass"] .hl-select [class*="trigger"]),
:deep(.folder-node-table [data-status-value="pass"] .hl-select [class*="button"]),
:deep(.folder-node-table [data-status-value="pass"] .hl-select > div:first-child),
:deep(.folder-node-table [data-status-value="pass"] .hl-select > button) {
  background-color: #dcfce7 !important;
  color: #16a34a !important;
  border: 1px solid #16a34a !important;
  border-radius: 0.375rem !important;
}

/* Blocked - Yellow */
:deep(.folder-node-table [data-status-value="blocked"] .hl-select [class*="trigger"]),
:deep(.folder-node-table [data-status-value="blocked"] .hl-select [class*="button"]),
:deep(.folder-node-table [data-status-value="blocked"] .hl-select > div:first-child),
:deep(.folder-node-table [data-status-value="blocked"] .hl-select > button) {
  background-color: #fef3c7 !important;
  color: #d97706 !important;
  border: 1px solid #d97706 !important;
  border-radius: 0.375rem !important;
}

/* Not Started - Gray */
:deep(.folder-node-table [data-status-value="not-started"] .hl-select [class*="trigger"]),
:deep(.folder-node-table [data-status-value="not-started"] .hl-select [class*="button"]),
:deep(.folder-node-table [data-status-value="not-started"] .hl-select > div:first-child),
:deep(.folder-node-table [data-status-value="not-started"] .hl-select > button) {
  background-color: #f3f4f6 !important;
  color: #6b7280 !important;
  border: 1px solid #6b7280 !important;
  border-radius: 0.375rem !important;
}

/* In Progress - Blue */
:deep(.folder-node-table [data-status-value="in-progress"] .hl-select [class*="trigger"]),
:deep(.folder-node-table [data-status-value="in-progress"] .hl-select [class*="button"]),
:deep(.folder-node-table [data-status-value="in-progress"] .hl-select > div:first-child),
:deep(.folder-node-table [data-status-value="in-progress"] .hl-select > button) {
  background-color: #dbeafe !important;
  color: #2563eb !important;
  border: 1px solid #2563eb !important;
  border-radius: 0.375rem !important;
}

/* Skipped - Purple */
:deep(.folder-node-table [data-status-value="skipped"] .hl-select [class*="trigger"]),
:deep(.folder-node-table [data-status-value="skipped"] .hl-select [class*="button"]),
:deep(.folder-node-table [data-status-value="skipped"] .hl-select > div:first-child),
:deep(.folder-node-table [data-status-value="skipped"] .hl-select > button) {
  background-color: #e9d5ff !important;
  color: #9333ea !important;
  border: 1px solid #9333ea !important;
  border-radius: 0.375rem !important;
}

/* Style all child elements inside status select */
:deep(.folder-node-table [data-status-value] .hl-select [class*="trigger"] *),
:deep(.folder-node-table [data-status-value] .hl-select [class*="trigger"] span),
:deep(.folder-node-table [data-status-value] .hl-select [class*="trigger"] div),
:deep(.folder-node-table [data-status-value] .hl-select [class*="trigger"] svg),
:deep(.folder-node-table [data-status-value] .hl-select [class*="button"] *),
:deep(.folder-node-table [data-status-value] .hl-select [class*="button"] span),
:deep(.folder-node-table [data-status-value] .hl-select [class*="button"] div),
:deep(.folder-node-table [data-status-value] .hl-select [class*="button"] svg) {
  color: inherit !important;
}

/* Assignee dropdown colors - Use CSS variables from wrapper */
:deep(.folder-node-table .assignee-select-wrapper .hl-select [class*="trigger"]),
:deep(.folder-node-table .assignee-select-wrapper .hl-select [class*="button"]),
:deep(.folder-node-table .assignee-select-wrapper .hl-select > div:first-child),
:deep(.folder-node-table .assignee-select-wrapper .hl-select > button) {
  background-color: var(--assignee-bg, #f3f4f6) !important;
  color: var(--assignee-text, #6b7280) !important;
  border: 1px solid var(--assignee-border, #6b7280) !important;
  border-radius: 0.375rem !important;
}

/* Style all child elements inside assignee select */
:deep(.folder-node-table .assignee-select-wrapper .hl-select [class*="trigger"] *),
:deep(.folder-node-table .assignee-select-wrapper .hl-select [class*="trigger"] span),
:deep(.folder-node-table .assignee-select-wrapper .hl-select [class*="trigger"] div),
:deep(.folder-node-table .assignee-select-wrapper .hl-select [class*="trigger"] svg),
:deep(.folder-node-table .assignee-select-wrapper .hl-select [class*="button"] *),
:deep(.folder-node-table .assignee-select-wrapper .hl-select [class*="button"] span),
:deep(.folder-node-table .assignee-select-wrapper .hl-select [class*="button"] div),
:deep(.folder-node-table .assignee-select-wrapper .hl-select [class*="button"] svg) {
  color: inherit !important;
}

/* Style tags if HLSelect renders them */
:deep(.folder-node-table .status-select .hl-tag),
:deep(.folder-node-table .assignee-select .hl-tag) {
  border-radius: 0.375rem !important;
  font-weight: 500 !important;
}

/* Hover effects for colored dropdowns */
:deep(.folder-node-table .hl-select .hl-select-trigger:hover) {
  filter: brightness(0.95) !important;
}

/* Focus state */
:deep(.folder-node-table .hl-select .hl-select-trigger:focus) {
  outline: 2px solid currentColor !important;
  outline-offset: 2px !important;
}
</style>